import { CheckCircle2, CircleDashed, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/cn';

// ─────────────────────────────────────────────────────────────────────────────
// SetupProgressStrip — the on-page replacement for the modal wizard's
// readiness row (Phase 4a). Pinned at the top of TournamentSetup, always
// visible, context-aware: the Organisation tab shows the ORG checklist
// (Org exists · Discord linked · Veto/branding set), every other tab shows
// the comp go-live checklist (Org linked · Competition created · Stages
// composed · Clans enrolled · Activated).
//
// Data comes from useOrgChecklist/useCompChecklist (lib/) — this component
// itself derives nothing; it just renders items and reports clicks upward.
// Clicking an item calls `onJump`, which TournamentSetup wires to switch tabs
// + fire useSectionHighlight's scroll/pulse/focus — see that file's header
// comment for the full mechanism.
// ─────────────────────────────────────────────────────────────────────────────

export type StripItem = {
  id: string;
  label: string;
  done: boolean;
  detail?: string;
  /** Informational step that must never gate progress (2026-07-22, added for
   *  the match-verification strip's optional map-veto step). Rendered muted and
   *  excluded from the ready/total count — an admin who legitimately skipped a
   *  step should not see the strip read as incomplete forever. Absent on every
   *  pre-existing caller, so their counts are unchanged. */
  optional?: boolean;
  /** On-page anchor id the caller's onJump can scroll to. Optional — the
   *  org/comp strips resolve their own targets via TournamentSetup's
   *  performJump instead and never read this. */
  sectionId?: string;
};

export function SetupProgressStrip({
  mode,
  items,
  onJump,
  title,
  warning,
}: {
  mode: 'org' | 'comp';
  items: StripItem[];
  onJump: (item: StripItem) => void;
  /** Overrides the mode-derived heading. Additive — omit for the org/comp
   *  strips and they read exactly as before. */
  title?: string;
  /** Loud note under the strip for a state the ticks alone can't express
   *  (e.g. a verified card whose result never reached standings). */
  warning?: string;
}) {
  const counted = items.filter(i => !i.optional);
  const readyCount = counted.filter(i => i.done).length;

  return (
    <div className="mb-4 rounded-xl bg-paper-panel px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-1 gap-y-1.5">
      <span className="mr-2 shrink-0 font-mono text-[10px] font-medium uppercase tracking-wider text-ink-meta">
        {title ?? (mode === 'org' ? 'Org setup' : 'Go-live checklist')} · {readyCount}/{counted.length} ready
      </span>
      {items.map((item, i) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onJump(item)}
          className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-left text-xs transition hover:bg-white/[0.05]"
        >
          {item.done
            ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-accent-500" strokeWidth={2.25} />
            : <CircleDashed className={cn('h-3.5 w-3.5 shrink-0', item.optional ? 'text-ink-meta/50' : 'text-ink-meta')} strokeWidth={2} />}
          <span className={cn('text-ink-secondary', item.done && 'text-ink-primary', item.optional && !item.done && 'text-ink-meta')}>
            {i + 1}. {item.label}
          </span>
          {item.detail && <span className="font-mono text-[10px] text-ink-meta">{item.detail}</span>}
        </button>
      ))}
      </div>
      {warning && (
        <p className="mt-2 flex items-start gap-1.5 px-2 font-mono text-[10px] leading-relaxed text-amber-300">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2.5} />
          {warning}
        </p>
      )}
    </div>
  );
}
